import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Avatar,
  Badge,
  Box,
  Card,
  CardContent,
  Checkbox,
  Dialog,
  FormControlLabel,
  Icon,
  IconButton,
  Typography,
  useTheme,
} from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FormHandles } from '@unform/core';
import { Form } from '../../../shared/forms';
import { useCallback, useEffect, useRef, useState } from 'react';
import * as Yup from 'yup';
import { VTextField, VTextFieldCNPJ } from '../../../shared/forms';
import getValidationErrors from '../../../shared/helpers/getValidationErrors';
import { IUpdateBusiness, businessService, feedback, finishRequest } from '../../../shared/services';
import { IPropsBusinessPage } from '../Business';
import { Button, FileUploader } from '../../../shared/components';
import { BusinessHoursConfig } from './BusinessHoursConfig';

const schema = Yup.object().shape({
  name: Yup.string().required(),
  address: Yup.string().required(),
  cnpj: Yup.string(),
  phone: Yup.string(),
  instagram: Yup.string(),
  facebook: Yup.string(),
  website: Yup.string(),
});

export const BusinessInformation: React.FC<IPropsBusinessPage> = ({ business, refetch }) => {
  const [businessProfilePicture, setBusinessProfilePicture] = useState<string>();
  const [openDialogUploadFile, setOpenDialogUploadFile] = useState(false);
  const [holidayModalOpen, setHolidayModalOpen] = useState(false);
  const [hideFromSearch, setHideFromSearch] = useState(business.hideFromSearch);

  const formRef = useRef<FormHandles>(null);
  const theme = useTheme();

  const { mutate: updateBusiness, isLoading: isLoadingUpdateBusiness } = useMutation((data: IUpdateBusiness) =>
    businessService.update(data), {
    onSuccess: () => finishRequest('Informações salvas', 'success', () => {
      refetch();
    }),
    onError: (error: Error) => finishRequest(error.message, 'error'),
  });

  const queryClient = useQueryClient();

  const { mutate: toggleHideFromSearch } = useMutation(
    (hidden: boolean) =>
      businessService.update({
        id: business.id,
        name: business.name,
        address: business.address,
        cnpj: business.cnpj,
        profilePicture: business.profilePicture,
        slug: business.slug,
        hideFromSearch: hidden,
        cardFeeDebit: business.cardFeeDebit,
        cardFeeCredit: business.cardFeeCredit,
        appointmentLeadTimeMinutes: business.appointmentLeadTimeMinutes,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['business']);
        refetch();
      },
      onError: (error: Error) => feedback(error.message, 'error'),
    }
  );

  const handleSubmit = useCallback(async (data: Record<string, unknown>) => {
    try {
      formRef.current?.setErrors({});

      await schema.validate({ name: data.name, address: data.address, cnpj: data.cnpj }, { abortEarly: false });

      const submitData: IUpdateBusiness = {
        id: business.id,
        name: data.name as string,
        slug: data.slug as string | undefined,
        address: data.address as string,
        cnpj: data.cnpj as string | undefined,
        profilePicture: business.profilePicture,
        hideFromSearch: hideFromSearch,
        cardFeeDebit: business.cardFeeDebit,
        cardFeeCredit: business.cardFeeCredit,
        appointmentLeadTimeMinutes: Number(data.appointmentLeadTimeMinutes) || 0,
        phone: (data.phone as string) || undefined,
        instagram: (data.instagram as string) || undefined,
        facebook: (data.facebook as string) || undefined,
        website: (data.website as string) || undefined,
      };

      updateBusiness(submitData);
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const errors = getValidationErrors(err as Yup.ValidationError);
        formRef.current?.setErrors(errors);
      }
    }
  }, [business, hideFromSearch]);

  useEffect(() => {
    setBusinessProfilePicture(business.profilePicture);
    setHideFromSearch(business.hideFromSearch);
  }, [business]);

  const onClose = () => {
    setOpenDialogUploadFile(false);
  };

  const onDelete = () => {
    setBusinessProfilePicture(undefined);
  };

  return (
    <Form key={business.id} ref={formRef} onSubmit={handleSubmit} initialData={business}>
      <Box display='flex' flexDirection='column' gap={2}>
        {/* Avatar + Link — sempre visível, fora dos accordions */}
        <Box display='flex' justifyContent='space-between' alignItems='start'>
          <Badge
            overlap="circular"
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            badgeContent={
              <IconButton onClick={() => setOpenDialogUploadFile(true)}>
                <Icon>photo_camera</Icon>
              </IconButton>
            }
          >
            <Avatar src={businessProfilePicture} slotProps={{ img: { loading: 'lazy' } }} sx={{ height: 150, width: 150 }} />
          </Badge>

          <IconButton
            onClick={() => {
              const redirectUrl = `${window.location.origin}/${business.slug || business.id}`;
              navigator.clipboard.writeText(redirectUrl);
              feedback('Link da página pública copiado para a área de transferência.', 'success');
            }}
          >
            <Icon>link</Icon>
          </IconButton>
        </Box>

        {/* Accordion: Informações Básicas */}
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography fontWeight={600}>Informações Básicas</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Card sx={{ bgcolor: theme.palette.background.paper }}>
              <CardContent>
                <Box display='flex' alignItems='center' justifyContent='space-between' gap={1}>
                  <Box>
                    <Typography variant='h6'>Modo de Teste</Typography>
                    <Typography variant='body2' color='text.secondary'>
                      {hideFromSearch
                        ? 'Sua barbearia está oculta para clientes — apenas você vê'
                        : 'Sua barbearia aparece nas buscas para clientes'}
                    </Typography>
                  </Box>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={hideFromSearch}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setHideFromSearch(checked);
                          toggleHideFromSearch(checked);
                        }}
                      />
                    }
                    label="Estou testando, não quero exibir para clientes"
                  />
                </Box>
              </CardContent>
            </Card>

            <VTextField name='name' label='Nome' required />
            <VTextField name='slug' label='Slug (link público)' helperText={`${window.location.origin}/${business.slug || '...'}`} />
            <VTextFieldCNPJ name='cnpj' label='CNPJ' />
            <VTextField name='address' label='Endereço' required />
          </AccordionDetails>
        </Accordion>

        {/* Accordion: Contato e Redes Sociais */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography fontWeight={600}>Contato e Redes Sociais</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <VTextField name='phone' label='Telefone' placeholder='(51) 99999-9999' />
            <VTextField name='instagram' label='Instagram' placeholder='@seudominio' />
            <VTextField name='facebook' label='Facebook' placeholder='https://facebook.com/seudominio' />
            <VTextField name='website' label='Website' placeholder='https://meusite.com.br' />
          </AccordionDetails>
        </Accordion>

        {/* Accordion: Horários de Funcionamento */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography fontWeight={600}>Horários de Funcionamento</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <BusinessHoursConfig business={business} refetch={refetch} mode='schedule' />

            <Button
              label='Adicionar Feriado'
              variant='outlined'
              fullWidth
              onClick={() => setHolidayModalOpen(true)}
            />
            <Dialog
              open={holidayModalOpen}
              onClose={() => setHolidayModalOpen(false)}
              maxWidth='sm'
              fullWidth
            >
              <Box p={3}>
                <Typography variant='h6' gutterBottom>
                  Feriados
                </Typography>
                <Typography variant='body2' color='text.secondary' mb={2}>
                  Adicione feriados para bloquear agendamentos em datas específicas.
                </Typography>
                <BusinessHoursConfig business={business} refetch={refetch} mode='holidays' />
              </Box>
            </Dialog>
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography fontWeight={600}>Configurações de Agendamento</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <VTextField
              name='appointmentLeadTimeMinutes'
              label='Tempo mínimo de antecedência (minutos)'
              type='number'
              helperText='Impede agendamentos com menos de X minutos de antecedência'
              isMinZero
            />
          </AccordionDetails>
        </Accordion>

        {/* Botão salvar — sempre visível no final */}
        <Box display='flex' justifyContent='end' pt={1}>
          <Button label='Salvar' variant='contained' type='submit' isLoading={isLoadingUpdateBusiness} />
        </Box>
      </Box>

      <FileUploader
        open={openDialogUploadFile}
        id={business.id}
        refetch={refetch}
        uploadFile={businessService.uploadFile}
        deleteFile={businessService.deleteFile}
        onDelete={onDelete}
        onClose={onClose}
      />
    </Form>
  );
};
